/* eslint-disable no-unused-vars */
import React, { useEffect, useLayoutEffect, useState, createContext } from "react";
import { setStatusBarStyle } from '@capacitor/status-bar';
import {
  ThemeProvider,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { Capacitor } from "@capacitor/core";
import { alpha } from "@mui/material/styles";
import { Translate as TranslateIcon } from "./icons";
import translateService from "./services/translateService";
import "./index.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import { auth, db } from "./firebase";
import TwoFactorChallenge from "./routes/TwoFactorChallenge";
import Login from "./routes/login";
import { T } from "./routes/Translate";
import Register from "./routes/register";
import Home from "./routes/home";
import AccountMenu from "./routes/AccountMenu";
import SavedItems from "./routes/SavedItems";
import Settings from "./routes/settings";
import Language from "./routes/Language";
import { languages } from "./routes/languages";
import Hilos from "./routes/Hilos";
import Events from "./routes/events";
import Members from "./routes/members";
import EditProfile from "./routes/EditProfile";
import Notifications from "./routes/notifications";
import ProfilePage from "./routes/ProfilePage";
import SettingNotification from "./routes/settingNotification";
import Terms from "./routes/terms";
import Policy from "./routes/privacyPolicy";
import EditEvents from "./routes/editEvents";
import EditOffers from "./routes/editOffers";
import EditCars from "./routes/editCars";
import EditTrips from "./routes/editTrips";
import MyPurchases from "./routes/MyPurchases";
import PublishOffer from "./routes/publishOffer";
import PublishEvents from "./routes/publishEvents";
import BackButton from "./routes/BackButton";
import NotificationIcon from "./routes/NotificationIcon";
import UserProfile from "./routes/UserProfile";
import ProfileHeader from "./routes/ProfileHeader";
import CopyToClipboardButton from "./routes/CopyToClipboardButton";
import BottomToolbar from "./routes/BottomToolbar";
import { endSession } from "./session";
import logo from "./imgs/logo192.png";
import Chats from "./routes/chats";
import Subscription from "./routes/subscription";
import Search from "./routes/search";
import CheckOut from "./routes/checkout";
import QRScannerPay from "./routes/QRScannerPay";
import PaymentQRGenerator from "./routes/PaymentQRGenerator";
import QRScanner from "./routes/QRScanner";
import CarsPage from "./routes/cars/cars";
import MyCars from "./routes/cars/myCars";
import Vehicles from "./routes/cars/vehicles";
import PublishCar from "./routes/cars/publishCar";
import CarDetail from "./routes/cars/CarDetail";
import FoodHome from "./routes/food/FoodHome";
import RestaurantPage from "./routes/food/RestaurantPage";
import RestaurantPanel from "./routes/food/RestaurantPanel";
import FoodOrderStatus from "./routes/food/FoodOrderStatus";
import MyFoodOrders from "./routes/food/MyFoodOrders";
import TripsPage from "./routes/trips/trips";
import Properties from "./routes/trips/myProperties";
import MyTrips from "./routes/trips/myTrips";
import PublishProperty from "./routes/trips/publishProperty";
import PropertyDetail from "./routes/trips/PropertyDetail";
import StayCheckout from "./routes/trips/StayCheckout";
import TripsHeader from "./routes/trips/TripsHeader";
import Support from "./routes/support";
import SupportChat from "./routes/SupportChat";
import UserMap from "./routes/UserMap";
import theme, { brandColors, barColors } from "./theme";
import PullToRefresh from "./components/PullToRefresh";
import ScrollManager from "./components/ScrollManager";
import { SupportAssistantProvider } from "./routes/SupportAssistantContext";
import SupportAssistantWidget from "./routes/SupportAssistantWidget";
import StoresSection from "./routes/stores";
import CreateStore from "./routes/stores/CreateStore";
import IOSPurchaseUnavailable from "./components/IOSPurchaseUnavailable";
import { isIOSApp } from "./utils/device";
import StoreDetail from "./routes/stores/StoreDetail";
import StoresDirectory from "./routes/stores/StoresDirectory";
import ProductDetail from "./routes/stores/ProductDetail";
import ProductCheckout from "./routes/stores/ProductCheckout";
import {
  SubscriptionProvider,
  useSubscriptionContext,
} from "./routes/SubscriptionContext";
import DirectChatBox from "./routes/Directchatbox";
import ResetPassword from "./routes/ResetPassword";
import AccountRecovery from "./routes/AccountRecovery";
import { usePushNotifications } from "./usePushNotifications";
import MyStores from "./routes/MyStores";
import AdminPanel from "./routes/AdminPanel";
import Refunds from "./routes/Refunds";
import DeleteAccount from "./routes/DeleteAccount";
import OfferDetail from "./routes/OfferDetail";
import OfferCheckout from "./routes/OfferCheckout";
import Explore from "./routes/Explore";
import PriceTracker from "./routes/PriceTracker";
import { isNativeGoogleSignInAvailable } from "./utils/nativeGoogleSignIn";
import ThreadDetail from "./routes/ThreadDetail";
import EventDetail from "./routes/EventDetail";
import { About, Faq, Contact } from "./routes/InfoPages";
import Statistics from "./routes/Statistics";
import { isAdminUser } from "./utils/adminAccess";
import Joyride from "react-joyride";
import DesktopSidebar from "./components/DesktopSidebar";
import { DesktopSidebarContext } from "./desktopSidebarContext";
import {
  DESKTOP_MEDIA_QUERY,
  SIDEBAR_WIDE_MEDIA_QUERY,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from "./pageLayout";
// Cambia esto: 
import { StatusBar, Style } from '@capacitor/status-bar';
// Por esto (que es el que sí se instaló):
import { NavigationBar } from '@capgo/capacitor-navigation-bar';
// Contexto para controlar el SpeedDial de publicar desde App.js (el tour de
// Joyride lo cierra al llegar a su paso) y desde PublishSpeedDial.js, que lo
// consume en cada pantalla donde vive embebido (home.js, events.js).
export const FloatingMenuContext = createContext({ speedDialOpen: false, setSpeedDialOpen: () => {} });

// Mismo color que la AppBar/BottomToolbar (barColors.background) y no el
// fondo de página (brandColors.backgroundDefault/Paper), para que el
// preloader haga juego con la status bar y la bottom nav en cada tema en
// vez de quedar como una franja de color distinta al entrar/salir de él.
export const Loading = () => (
  <div
    className="loading-container"
    style={{
      backgroundColor: barColors.background,
      "--loading-bar-track": alpha(barColors.text, 0.15),
      "--loading-bar-fill": barColors.text,
    }}
  >
    <img className="loading-img" src={logo} alt="Notiline" />
    <div className="loading-bar">
      <div className="loading-bar-fill" />
    </div>
  </div>
);

const updateStatusBar = async () => {
  // Para cambiar el estilo (Iconos claros u oscuros)
  await StatusBar.setStyle({ style: Style.Dark }); // 'Dark' hace los iconos blancos

  // Para cambiar el color de fondo (La barra de arriba)
  await StatusBar.setBackgroundColor({ color: '#030553' }); 
};

const updateBars = async () => {
  // Barra de arriba (Status Bar)
  await StatusBar.setBackgroundColor({ color: '#030553' });

  // Barra de abajo (Navigation Bar - la que querías azul)
  await NavigationBar.set({
    color: '#030553',
    darkButtons: false // Iconos blancos
  });
};

const ProtectedRoute = ({ user, appLoading, children }) => {
  const location = useLocation();

  if (appLoading) {
    return <Loading />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AdminRoute = ({ user, appLoading, children }) => {
  const location = useLocation();

  if (appLoading) {
    return <Loading />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdminUser(user)) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

const ProtectedPublish = ({ user, appLoading, children }) => {
  const { isSubscriptionActive } = useSubscriptionContext();
  const location = useLocation();

  if (appLoading) {
    return <Loading />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isSubscriptionActive) {
    // En iOS no se venden planes: se avisa acá en vez de mandar a /subscription.
    if (isIOSApp()) return <IOSPurchaseUnavailable />;
    return <Navigate to="/subscription" state={{ from: location }} replace />;
  }

  return children;
};

// Redirects de compatibilidad para URLs viejas en español con parámetros
// dinámicos (un <Navigate to="..."> estático no puede armar el path nuevo
// a partir de :uid/:storeSlug/etc, así que cada uno arma el destino con el
// param real y redirige).
const RedirectProfile = () => {
  const { uid } = useParams();
  return <Navigate to={`/profile/${uid}`} replace />;
};
const RedirectCarDetail = () => {
  const { carId } = useParams();
  return <Navigate to={`/cars/${carId}`} replace />;
};
const RedirectPropertyDetail = () => {
  const { placeId } = useParams();
  return <Navigate to={`/trips/${placeId}`} replace />;
};
// El pago de autos y lugares desde el chat (/checkout/auto|viaje/:id) ya no
// existe: autos y propiedades se contactan por chat/WhatsApp y los
// alojamientos se reservan en /trips/:placeId/reservar.
const RedirectOldCheckout = () => {
  const { type, itemId } = useParams();
  return <Navigate to={type === "auto" ? `/cars/${itemId}` : `/trips/${itemId}`} replace />;
};
const RedirectStore = () => {
  const { storeSlug } = useParams();
  return <Navigate to={`/stores/${storeSlug}`} replace />;
};
const RedirectStoreProduct = () => {
  const { storeSlug, productId } = useParams();
  return <Navigate to={`/stores/${storeSlug}/products/${productId}`} replace />;
};
const RedirectStoreProductPayment = () => {
  const { storeSlug, productId } = useParams();
  return <Navigate to={`/stores/${storeSlug}/products/${productId}/payment`} replace />;
};

// Asistente IA flotante en toda la app, solo con sesión (la función
// aiAssistant la exige). No va en pantallas con su propia caja de texto
// abajo, ni en login/registro.
const ASSISTANT_HIDDEN_PREFIXES = ["/support/chat", "/chatbox", "/login", "/register", "/reset-password", "/account-recovery", "/admin"];
const GlobalAssistant = ({ user }) => {
  const { pathname } = useLocation();
  if (!user || ASSISTANT_HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  return <SupportAssistantWidget />;
};

const AppRoutes = ({ user, appLoading, handleLogout }) => {
  return (
    <SupportAssistantProvider>
      <Routes>
        {/* Rutas públicas */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacyPolicy" element={<Policy />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account-recovery" element={<AccountRecovery />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/precios" element={<PriceTracker />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/contact" element={<Contact />} />
        {/* Fichas públicas de publicaciones (Google/AdSense y visitantes sin cuenta) */}
        <Route path="/offers/:offerId" element={<OfferDetail />} />
        <Route path="/events/:eventId" element={<EventDetail />} />
        <Route path="/hilos/:threadId" element={<ThreadDetail />} />

        {/* Ruta raíz */}
        <Route
          path="/language"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Language />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            appLoading ? (
              <Loading />
            ) : user ? (
              <Home handleLogout={handleLogout} />
            ) : isNativeGoogleSignInAvailable() ? (
              // En la app Android el inicio sin sesión sigue siendo el login
              <Navigate to="/login" replace />
            ) : (
              // En la web los visitantes ven contenido (Explorar), no un login:
              // es lo primero que revisan Google y AdSense
              <Explore />
            )
          }
        />

        {/* Rutas protegidas básicas */}
        <Route
          path="/home"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Home handleLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <AccountMenu />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/statistics"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Statistics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-stores"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <MyStores />
            </ProtectedRoute>
          }
        />
        <Route
          path="/saved"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <SavedItems />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Events />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hilos"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Hilos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/editEvents"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <EditEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/copy"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <CopyToClipboardButton />
            </ProtectedRoute>
          }
        />
        <Route
          path="/back"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <BackButton />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Support />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/chat"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <SupportChat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stores"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <StoresSection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stores/create"
          element={
            isIOSApp() ? <IOSPurchaseUnavailable /> : (
            <ProtectedPublish user={user} appLoading={appLoading}>
              <CreateStore />
            </ProtectedPublish>
            )
          }
        />
        <Route path="/store-directory" element={<StoresDirectory />} />
        <Route path="/stores/:storeSlug" element={<StoreDetail />} />
        <Route
          path="/stores/:storeSlug/products/:productId"
          element={<ProductDetail />}
        />
        <Route
          path="/stores/:storeSlug/products/:productId/payment"
          element={<ProductCheckout />}
        />
        <Route
          path="/ProfileHeader"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <ProfileHeader />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              {isIOSApp() ? <IOSPurchaseUnavailable /> : <Subscription />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/chats"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Chats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/map"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <UserMap />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/:userId"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <UserProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chatbox/:type/:itemId/:otherUserId"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <DirectChatBox />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chatbox/directo/:otherUserId"
          element={user ? <DirectChatBox /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/QRScannerPay"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <QRScannerPay />
            </ProtectedRoute>
          }
        />
        <Route
          path="/PaymentQRGenerator"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <PaymentQRGenerator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/QRScanner"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <QRScanner />
            </ProtectedRoute>
          }
        />
        <Route
          path="/BottomToolbar"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <BottomToolbar />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Search />
            </ProtectedRoute>
          }
        />
        <Route
          path="/NotificationIcon"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <NotificationIcon />
            </ProtectedRoute>
          }
        />
        <Route
          path="/editOffers"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <EditOffers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/editCars"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <EditCars />
            </ProtectedRoute>
          }
        />
        <Route
          path="/editTrips"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <EditTrips />
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers/:offerId/checkout"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <OfferCheckout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-purchases"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <MyPurchases />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settingNotification"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <SettingNotification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/members"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Members />
            </ProtectedRoute>
          }
        />
        <Route path="/checkout/:type/:itemId" element={<RedirectOldCheckout />} />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <CheckOut />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:uid"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <EditProfile />
            </ProtectedRoute>
          }
        />

        {/* Pedidos: comprar solo pide login; tener un local es parte del plan */}
        <Route
          path="/pedidos"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <FoodHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pedidos/mis-pedidos"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <MyFoodOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pedidos/mi-local"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <RestaurantPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pedidos/orden/:orderId"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <FoodOrderStatus />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pedidos/:restaurantId"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <RestaurantPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cars"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <CarsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/myCars"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <MyCars />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vehicles"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Vehicles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/CarDetail"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <CarDetail />
            </ProtectedRoute>
          }
        />
        {/* Pública: Google (AdSense/búsqueda) y visitantes sin cuenta */}
        <Route path="/cars/:carId" element={<CarDetail />} />

        {/* Rutas de viajes protegidas */}
        <Route
          path="/trips"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <TripsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Properties"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <Properties />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-trips"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <MyTrips />
            </ProtectedRoute>
          }
        />
        {/* Pública: Google (AdSense/búsqueda) y visitantes sin cuenta */}
        <Route path="/trips/:placeId" element={<PropertyDetail />} />
        <Route
          path="/trips/:placeId/reservar"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <StayCheckout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/PropertyDetail"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <PropertyDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/TripsHeader"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <TripsHeader />
            </ProtectedRoute>
          }
        />

        {/* Rutas que requieren suscripción */}
        <Route
          path="/publishOffer"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <ProtectedPublish user={user} appLoading={appLoading}>
                <PublishOffer />
              </ProtectedPublish>
            </ProtectedRoute>
          }
        />
        <Route
          path="/publishEvents"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <ProtectedPublish user={user} appLoading={appLoading}>
                <PublishEvents />
              </ProtectedPublish>
            </ProtectedRoute>
          }
        />
        <Route
          path="/publishProperty"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <ProtectedPublish user={user} appLoading={appLoading}>
                <PublishProperty />
              </ProtectedPublish>
            </ProtectedRoute>
          }
        />
        <Route
          path="/publishCar"
          element={
            <ProtectedRoute user={user} appLoading={appLoading}>
              <ProtectedPublish user={user} appLoading={appLoading}>
                <PublishCar />
              </ProtectedPublish>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <AdminRoute user={user} appLoading={appLoading}>
              <AdminPanel />
            </AdminRoute>
          }
        />

        {/* Redirects de compatibilidad: URLs viejas en español (links ya
            compartidos, notificaciones push ya enviadas, favoritos
            guardados) que ahora tienen un equivalente en inglés. Sin esto,
            cualquier link viejo que ya esté circulando dejaría de andar. */}
        <Route path="/idioma" element={<Navigate to="/language" replace />} />
        <Route path="/ajustes" element={<Navigate to="/settings" replace />} />
        <Route path="/estadisticas" element={<Navigate to="/statistics" replace />} />
        <Route path="/mis-tiendas" element={<Navigate to="/my-stores" replace />} />
        <Route path="/guardados" element={<Navigate to="/saved" replace />} />
        <Route path="/atras" element={<Navigate to="/back" replace />} />
        <Route path="/tiendas/crear" element={<Navigate to="/stores/create" replace />} />
        <Route path="/tiendas/:storeSlug/productos/:productId/pago" element={<RedirectStoreProductPayment />} />
        <Route path="/tiendas/:storeSlug/productos/:productId" element={<RedirectStoreProduct />} />
        <Route path="/tiendas/:storeSlug" element={<RedirectStore />} />
        <Route path="/tiendas" element={<Navigate to="/stores" replace />} />
        <Route path="/directorio-tiendas" element={<Navigate to="/store-directory" replace />} />
        <Route path="/HeaderPerfil" element={<Navigate to="/ProfileHeader" replace />} />
        <Route path="/CobrarCQR" element={<Navigate to="/PaymentQRGenerator" replace />} />
        <Route path="/vehiculos" element={<Navigate to="/vehicles" replace />} />
        <Route path="/VerAuto" element={<Navigate to="/CarDetail" replace />} />
        <Route path="/autos/ver/:carId" element={<RedirectCarDetail />} />
        <Route path="/viajes" element={<Navigate to="/trips" replace />} />
        <Route path="/Trips" element={<Navigate to="/my-trips" replace />} />
        <Route path="/viajes/ver/:placeId" element={<RedirectPropertyDetail />} />
        <Route path="/VerLugar" element={<Navigate to="/PropertyDetail" replace />} />
        <Route path="/HeaderViajes" element={<Navigate to="/TripsHeader" replace />} />
        <Route path="/perfil/:uid" element={<RedirectProfile />} />

        {/* Ruta para manejar URLs no encontradas */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <GlobalAssistant user={user} />
    </SupportAssistantProvider>
  );
};

// Vive afuera de #app-shell (ver App) para que el bloque de refresh quede
// fijo respecto de la ventana en vez de desplazarse junto con el contenido
// que empuja hacia abajo. Sigue necesitando estar dentro de <Router> por
// useLocation, así que se monta como hermano del shell, no como hijo suyo.
const PullToRefreshMount = () => {
  const location = useLocation();
  // El gesto compite con el drag para mover el mapa (Leaflet) y para rotar
  // el globo 3D de viajes (react-globe.gl): en ambos, arrastrar hacia abajo
  // terminaba disparando un refresh de página en vez de mover el mapa/globo.
  // Se desactiva en esas rutas.
  const disabled =
    location.pathname.startsWith("/map") || location.pathname.startsWith("/trips");

  return (
    <PullToRefresh onRefresh={() => window.location.reload()} disabled={disabled} />
  );
};

// Pantallas de acceso: van a pantalla completa, sin sidebar aunque haya
// sesión (p.ej. /login justo antes de redirigir, o el QR que es un overlay).
const SIDEBAR_HIDDEN_PATHS = [
  "/login",
  "/register",
  "/reset-password",
  "/account-recovery",
  "/welcomeSlides",
  "/QRScanner",
];
const isNativeApp = Capacitor.isNativePlatform();

const AppContent = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  // Versión web de escritorio: la navegación (AppBar de categorías +
  // BottomToolbar) pasa a una sidebar fija a la izquierda. Solo con sesión
  // iniciada: los visitantes siguen con el layout público de siempre.
  // noSsr: así el primer render ya sale con el layout correcto (sin CLS).
  const isDesktopWeb = useMediaQuery(DESKTOP_MEDIA_QUERY, { noSsr: true }) && !isNativeApp;
  const sidebarWide = useMediaQuery(SIDEBAR_WIDE_MEDIA_QUERY, { noSsr: true });
  const sidebarActive =
    isDesktopWeb && !!user && !SIDEBAR_HIDDEN_PATHS.includes(location.pathname);
  const sidebarWidth = sidebarActive ? (sidebarWide ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH) : 0;

  // --sidebar-w + la clase en <body> son lo que usa index.css para correr a
  // la derecha de la sidebar los AppBar fijos de cada pantalla (cada una arma
  // el suyo) y dejarlos planos, sin tocar pantalla por pantalla.
  useLayoutEffect(() => {
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty("--sidebar-w", `${sidebarWidth}px`);
    rootStyle.setProperty("--page-bg", brandColors.backgroundDefault);
    rootStyle.setProperty("--page-text", brandColors.textPrimary);
    rootStyle.setProperty("--page-divider", brandColors.divider);
    document.body.classList.toggle("has-desktop-sidebar", sidebarActive);
  }, [sidebarActive, sidebarWidth]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(
    translateService.getLanguage(),
  );
  // Mostrar Joyride solo a los recién registrados
  const [joyrideRun, setJoyrideRun] = useState(false);

  // Detección automática de idioma según el país (solo en el primer
  // ingreso, ver autoDetectLanguageByCountry): si cambia, recarga para
  // que se apliquen las traducciones.
  useEffect(() => {
    translateService.autoDetectLanguageByCountry().then((changed) => {
      if (changed) window.location.reload();
    });
  }, []);

  useEffect(() => {
    // Solo mostrar el tour si el usuario está autenticado y nunca lo vio
    if (user && !localStorage.getItem('notiline_joyride_shown')) {
      setJoyrideRun(true);
      localStorage.setItem('notiline_joyride_shown', '1');
    } else {
      setJoyrideRun(false);
    }
  }, [user]);
  // Estado global para controlar el SpeedDial de publicar
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const joyrideCallback = (data) => {
    // Si el paso actual es el de publicar, forzar cerrar el SpeedDial
    if (data.step && data.step.target === "#tour-publicar" && speedDialOpen) {
      setSpeedDialOpen(false);
    }
    // Scroll manual para cupón, copiar y QRScanner
    if (data.step && ["#tour-cupon", "#tour-copiar", "#tour-qrscanner"].includes(data.step.target)) {
      setTimeout(() => {
        const el = document.querySelector(data.step.target);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 400); // Espera a que Joyride intente enfocar primero
    }
  };
  const joyrideSteps = [
    {
      target: "body",
      placement: "center",
      title: "Bienvenido a Notiline",
      content:
        "Esta es una guía rápida de la interfaz. Usa los botones para navegar y descubrir funciones.",
      disableBeacon: true,
    },
    {
      target: "#tour-home",
      title: "Inicio",
      content: "Haz clic aquí para volver a la pantalla principal en cualquier momento.",
    },
    {
      target: "#tour-search",
      title: "Buscar",
      content: "Busca productos, tiendas o usuarios desde aquí.",
    },
    {
      target: "#tour-notifications",
      title: "Notificaciones",
      content: "Consulta tus notificaciones importantes aquí.",
    },
    {
      target: "#tour-profile",
      title: "Perfil",
      content: "Accede a tu perfil y configuración personal.",
    },
    {
      target: "#tour-publicar",
      title: "Publicar",
      content: "Haz clic aquí para abrir el menú de publicación y crear ofertas, eventos, autos o propiedades y tu tienda.",
    },
    {
      target: "#tour-descuento",
      title: "Descuento",
      content: "Aquí puedes ver el porcentaje de descuento disponible en la oferta.",
    },
    {
      target: "#tour-cupon",
      title: "Cupón",
      content: "Aquí está el cupón que puedes copiar y usar en tu compra.",
    },
    {
      target: "#tour-copiar",
      title: "Botón Copiar",
      content: "Haz clic aquí para copiar el cupón al portapapeles.",
    },
    {
      target: "#tour-comprar",
      title: "Comprar",
      content: "Haz clic aquí para comprar usando el cupón o descuento.",
    },
    {
      target: "#tour-chat",
      title: "Chat",
      content: "Accede a tus chats y mensajes directos aquí.",
    },
    {
      target: "#tour-etiqueta",
      title: "Ofertas",
      content: "Publica o consulta ofertas disponibles.",
    },
    {
      target: "#tour-calendario",
      title: "Eventos",
      content: "Publica o consulta eventos próximos.",
    },
    {
      target: "#tour-auto",
      title: "Autos",
      content: "Publica o consulta autos disponibles.",
    },
    {
      target: "#tour-avion",
      title: "Lugares",
      content: "Publica o consulta alojamientos, propiedades en venta y alquileres.",
    },
    {
      target: "#tour-qrscanner-btn",
      title: "QR",
      content: "Tocá acá para escanear un QR y cobrar cupones o pagos. Con suscripción también podés generar tu QR de cobro.",
    },
    {
      target: "#tour-chats-btn",
      title: "Chats",
      content: "Accede a tus chats y mensajes directos desde este botón flotante.",
    },
    // Puedes agregar más pasos según los elementos clave de tu UI
  ];

  usePushNotifications();

  const handleTranslateClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTranslateClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (langCode) => {
    translateService.setLanguage(langCode);
    setCurrentLanguage(langCode);
    handleTranslateClose();

    // Recargar la página para aplicar traducciones
    window.location.reload();
  };

  const changeLanguageToAuto = () => {
    handleTranslateClose();
    translateService.setAutoLanguage().finally(() => window.location.reload());
  };

  // 'checking' | 'required' | 'clear' — sólo relevante cuando twoFactorEnabled
  // está activo en el doc del usuario; se resuelve una vez por dispositivo (ver
  // localStorage abajo) y sólo se vuelve a pedir tras cerrar sesión en ese dispositivo.
  const [twoFactorStatus, setTwoFactorStatus] = useState('clear');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      setTwoFactorStatus('clear');
      return;
    }
    const sessionKey = `2fa_verified_${user.uid}`;
    if (localStorage.getItem(sessionKey)) {
      setTwoFactorStatus('clear');
      return;
    }
    let active = true;
    setTwoFactorStatus('checking');
    db.doc(`users/${user.uid}`).get().then((snap) => {
      if (!active) return;
      const requires2fa = snap.exists && !!snap.data().twoFactorEnabled;
      setTwoFactorStatus(requires2fa ? 'required' : 'clear');
    }).catch((error) => {
      console.error("Error al verificar 2FA:", error);
      if (active) setTwoFactorStatus('clear');
    });
    return () => { active = false; };
  }, [user]);

  const handleLogout = async () => {
    try {
      await endSession();
      setUser(null);
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (user && twoFactorStatus === 'checking') {
    return <Loading />;
  }

  if (user && twoFactorStatus === 'required') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <TwoFactorChallenge
          user={user}
          onSuccess={() => {
            localStorage.setItem(`2fa_verified_${user.uid}`, '1');
            setTwoFactorStatus('clear');
          }}
          onCancel={handleLogout}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Botón de traducción solo en rutas públicas específicas */}
      {(() => {
        const location = window.location.pathname;
        const showTranslate = [
          "/login",
          "/register",
          "/reset-password",
          "/account-recovery",
          "/welcomeSlides",
        ].includes(location);
        if (!showTranslate) return null;
        return (
          <div
            style={{
              position: "fixed",
              top: "15px",
              right: "15px",
              zIndex: 10001,
            }}
          >
            <IconButton
              onClick={handleTranslateClick}
              style={{
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                padding: "3px",
              }}
              title={<T>Traducir página</T>}
            >
              <TranslateIcon color="primary" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleTranslateClose}
              PaperProps={{
                style: {
                  maxHeight: 400,
                  width: "250px",
                },
              }}
            >
              <MenuItem
                onClick={changeLanguageToAuto}
                selected={translateService.isAutoLanguage()}
              >
                <span style={{ marginRight: "10px", fontSize: "20px" }}>🌐</span>
                <T>Automático (según ubicación)</T>
              </MenuItem>
              {languages.map((language) => (
                <MenuItem
                  key={language.code}
                  onClick={() => changeLanguage(language.code)}
                  selected={!translateService.isAutoLanguage() && currentLanguage === language.code}
                >
                  <span style={{ marginRight: "10px", fontSize: "20px" }}>
                    {language.flag}
                  </span>
                  {language.name}
                </MenuItem>
              ))}
            </Menu>
          </div>
        );
      })()}

      <>
        {/* #app-shell es el ancestro que PullToRefresh empuja hacia abajo
            (transform imperativo, ver PullToRefresh.js). Un transform en un
            ancestro pasa a ser el "containing block" de sus descendientes
            position:fixed, así que basta este único wrapper para que TODOS
            los AppBar fijos de cada ruta (cada una arma el suyo) y el
            BottomToolbar se desplacen juntos, sin tocar pantalla por
            pantalla. PullToRefreshMount queda afuera a propósito para no
            moverse con el resto.
            flow-root evita que el margen superior del contenido (p. ej. el
            del feed de Home bajo su AppBar) colapse hacia afuera: si no,
            #app-shell empieza más abajo del borde y, con el transform, los
            AppBar fijos se corrían ese margen de más (quedaba un hueco). */}
        <div id="app-shell" style={{ display: 'flow-root', ...(sidebarActive ? { paddingLeft: sidebarWidth } : {}) }}>
          <DesktopSidebarContext.Provider value={sidebarActive}>
          <SubscriptionProvider>
            <FloatingMenuContext.Provider value={{ speedDialOpen, setSpeedDialOpen }}>
              {sidebarActive && (
                <DesktopSidebar width={sidebarWidth} collapsed={!sidebarWide} />
              )}
              <AppRoutes
                user={user}
                appLoading={loading}
                handleLogout={handleLogout}
              />
              <Joyride
                steps={joyrideSteps}
                run={joyrideRun}
                continuous
                showSkipButton
                showProgress
                styles={{ options: { zIndex: 20000 } }}
                locale={{
                  back: 'Atrás',
                  close: 'Cerrar',
                  last: 'Finalizar',
                  next: 'Siguiente',
                  skip: 'Saltar',
                }}
                callback={joyrideCallback}
              />
            </FloatingMenuContext.Provider>
          </SubscriptionProvider>
          </DesktopSidebarContext.Provider>
        </div>
        <PullToRefreshMount />
        <ScrollManager />
      </>
    </ThemeProvider>
  );
};

// El Router va por fuera de AppContent para que no se desmonte cuando
// AppContent muestra <Loading /> (p. ej. el chequeo de 2FA que corre apenas
// cambia el usuario). Si se desmontaba, un navigate() hecho en ese momento
// (como el de login con Google) cambiaba la URL pero la pantalla seguía en
// /login hasta recargar.
const App = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <AppContent />
  </Router>
);

export default App;
